import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

/**
 * Dialog para crear una nueva garantia D2D.
 *
 * Validaciones:
 *   - nombre_cliente: requerido
 *   - cuenta:         12 digitos numericos (mismo patron que add-client)
 *   - telefono:       7-8 digitos
 *   - id_tipo_servicio: requerido (dropdown Internet/TV/Telefonia, desde
 *     tabla garantia_d2d_tipo_servicio)
 *   - id_tipo_problema: requerido, dropdown dependiente del servicio
 *     elegido (tabla garantia_d2d_tipo_problema)
 *   - comentario_creacion: requerido
 *
 * El backend revalida. Los catalogos se cargan al abrir el dialog (1
 * request para servicios y luego 1 por cada cambio de servicio).
 */
@Component({
  selector: 'app-crear-garantia-dialog',
  templateUrl: './crear-garantia-dialog.component.html',
  styleUrls: ['./crear-garantia-dialog.component.scss']
})
export class CrearGarantiaDialogComponent implements OnInit {

  form: FormGroup;
  guardando = false;

  servicios: any[] = [];
  problemas: any[] = [];
  cargandoServicios = false;
  cargandoProblemas = false;

  constructor(
    private dialogRef: MatDialogRef<CrearGarantiaDialogComponent>,
    private gateway: GatewayService,
    private localSave: LocalSaveService,
    private snackbar: MatSnackBar,
  ) {
    this.form = new FormGroup({
      nombre_cliente:      new FormControl('', [Validators.required, Validators.maxLength(255)]),
      cuenta:              new FormControl('', [Validators.required, Validators.minLength(12), Validators.maxLength(12), Validators.pattern(/^\d{12}$/)]),
      telefono:            new FormControl('', [Validators.required, Validators.pattern(/^\d{7,8}$/)]),
      id_tipo_servicio:    new FormControl(null, [Validators.required]),
      id_tipo_problema:    new FormControl({ value: null, disabled: true }, [Validators.required]),
      comentario_creacion: new FormControl('', [Validators.required, Validators.maxLength(1000)]),
    });
  }

  ngOnInit(): void {
    this.cargarServicios();
    // Cuando cambia el servicio, recargar problemas y limpiar la seleccion.
    this.form.controls.id_tipo_servicio.valueChanges.subscribe((idServ: number | null) => {
      const probCtrl = this.form.controls.id_tipo_problema;
      probCtrl.setValue(null);
      this.problemas = [];
      if (idServ) {
        probCtrl.enable();
        this.cargarProblemas(idServ);
      } else {
        probCtrl.disable();
      }
    });
  }

  private cargarServicios(): void {
    this.cargandoServicios = true;
    this.gateway.get('garantiaD2dListarServicios', {})
      .then((res: any) => {
        this.cargandoServicios = false;
        this.servicios = res && res.rows ? res.rows : [];
      })
      .catch(() => {
        this.cargandoServicios = false;
        this.snackbar.open('Error al cargar servicios.', 'Cerrar', { duration: 3000 });
      });
  }

  private cargarProblemas(idServicio: number): void {
    this.cargandoProblemas = true;
    this.gateway.get('garantiaD2dListarProblemas', { id_tipo_servicio: idServicio })
      .then((res: any) => {
        this.cargandoProblemas = false;
        this.problemas = res && res.rows ? res.rows : [];
      })
      .catch(() => {
        this.cargandoProblemas = false;
        this.snackbar.open('Error al cargar problemas.', 'Cerrar', { duration: 3000 });
      });
  }

  guardar(): void {
    if (this.form.invalid || this.guardando) { return; }
    this.guardando = true;
    const user = this.localSave.get('usuarioPGV') || {};
    const obj = user.OBJ || {};
    const nombreCombinado = ((obj.nombre || '') + ' ' + (obj.apellido || '')).trim();
    const payload = {
      nombre_cliente:      this.form.controls.nombre_cliente.value,
      cuenta:              String(this.form.controls.cuenta.value || '').trim(),
      telefono:            String(this.form.controls.telefono.value || '').trim(),
      id_tipo_servicio:    this.form.controls.id_tipo_servicio.value,
      id_tipo_problema:    this.form.controls.id_tipo_problema.value,
      comentario_creacion: this.form.controls.comentario_creacion.value,
      // Snapshot del creador desde localStorage. Admin/Supervisor/Distribuidor
      // no estan en tabla agente, asi que el frontend resuelve nombre+correo y
      // los pasa al backend para grabar el snapshot.
      id_agente_creador:   user.idagente != null ? user.idagente : user.IDU,
      nombre_creador:      nombreCombinado || this.localSave.get('userLoggedIn') || null,
      correo_creador:      obj.correo || null,
    };
    this.gateway.get('garantiaD2dCrear', payload)
      .then((res: any) => {
        this.guardando = false;
        if (Number(res.response) === 1) {
          this.dialogRef.close({ creada: true, id: res.id });
        } else {
          this.snackbar.open(res.message || 'No se pudo crear.', 'Cerrar', { duration: 4000 });
        }
      })
      .catch(() => {
        this.guardando = false;
        this.snackbar.open('Error al crear la garantía.', 'Cerrar', { duration: 3000 });
      });
  }

  cerrar(): void {
    this.dialogRef.close({ creada: false });
  }
}
